import { useState } from "react";
import { format, parseISO, type Locale } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import type { DayPickerBase } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface DatePickerProps {
  /** Put on the trigger button, so a `<Label htmlFor>` can name the field. */
  id?: string;
  /** ISO date string (`yyyy-MM-dd`) or null. */
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** `md` (default) — dialog-density. `sm` — compact for inline editors.
   *  `lg` — ~35% larger for the expanded issue panel's meta bar. */
  size?: "sm" | "md" | "lg";
  clearLabel?: string;
  /** date-fns pattern for the trigger's label. The issue detail panel passes
   *  `dd MMM yyyy` so every date in the panel reads day-month-year (WHA-3810). */
  displayFormat?: string;
  /** date-fns locale for the trigger label and calendar. Omit for English. */
  locale?: Locale;
  /** Extra marks on the calendar, e.g. the other end of a date range. A
   *  `defaultMonth` here wins over the selected date's month. */
  calendarProps?: Pick<
    DayPickerBase,
    "defaultMonth" | "modifiers" | "modifiersClassNames" | "components"
  >;
}

function parseValue(value: string | null): Date | undefined {
  if (!value) return undefined;
  try {
    return parseISO(value);
  } catch {
    return undefined;
  }
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  className,
  size = "md",
  clearLabel = "Clear",
  displayFormat = "dd.MM.yyyy",
  locale,
  calendarProps,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseValue(value);

  const handleSelect = (date: Date | undefined) => {
    if (!date) {
      onChange(null);
    } else {
      onChange(format(date, "yyyy-MM-dd"));
    }
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setOpen(false);
  };

  const sizeClasses = {
    sm: "h-7 px-2 py-0 text-xs font-normal",
    md: "h-10 px-3 py-2 text-sm font-normal",
    lg: "h-9 px-2.5 py-0 text-[15px] font-normal",
  }[size];
  const iconClass = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  }[size];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start gap-2",
            sizeClasses,
            !selected && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className={iconClass} />
          {selected ? format(selected, displayFormat, { locale }) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          {...calendarProps}
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          locale={locale}
          // react-day-picker opens on today whatever is selected; open on the
          // chosen date instead.
          defaultMonth={calendarProps?.defaultMonth ?? selected}
          initialFocus
        />
        {selected && (
          <div className="flex items-center justify-end border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
              {clearLabel}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
